import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash } from '@phosphor-icons/react';
import { useAuth } from '../hooks/useAuth';
import '../pages/ProviderDashboard.css';
import './WorkerEdit.css';

const workerClassifications = [
  'Domestic Assistance Worker',
  'Home & Garden Worker',
  'Support Worker',
  'Personal Care Worker',
  'Allied Health Assistant',
  'Enrolled Nurse (EN)',
  'Registered Nurse (RN)',
  'Occupational Therapist',
  'Physiotherapist',
  'Speech Pathologist',
  'Psychologist',
  'Social Worker',
  'Exercise Physiologist',
  'Dietitian',
  'Podiatrist',
  'Behaviour Support Practitioner'
];

const workerChecks = [
  'NDIS Worker Screening Check',
  'National Police Check',
  'Working With Children Check',
  'Driver Licence',
  'First Aid',
  'CPR'
];

const qualificationLevels = [
  'Certificate II',
  'Certificate III',
  'Certificate IV',
  'Diploma',
  'Advanced Diploma',
  'Bachelor Degree',
  'Graduate Certificate',
  'Graduate Diploma',
  'Masters Degree',
  'Doctorate'
];

const qualificationFields = [
  'Individual Support',
  'Aged Care',
  'Disability Support',
  'Ageing Support',
  'Community Services',
  'Mental Health',
  'Allied Health Assistance',
  'Nursing',
  'Occupational Therapy',
  'Physiotherapy',
  'Speech Pathology',
  'Psychology',
  'Social Work',
  'Exercise Physiology',
  'Dietetics',
  'Podiatry',
  'Behaviour Support',
  'Other'
];

const registrationTypes = [
  'AHPRA Enrolled Nurse',
  'AHPRA Registered Nurse',
  'AHPRA Occupational Therapist',
  'AHPRA Physiotherapist',
  'AHPRA Psychologist',
  'AHPRA Podiatrist',
  'Speech Pathology Australia',
  'ESSA Accredited Exercise Physiologist',
  'Accredited Practising Dietitian',
  'AASW Social Worker',
  'NDIS Behaviour Support Practitioner'
];

const registrationClassifications = new Set([
  'Enrolled Nurse (EN)',
  'Registered Nurse (RN)',
  'Occupational Therapist',
  'Physiotherapist',
  'Speech Pathologist',
  'Psychologist',
  'Social Worker',
  'Exercise Physiologist',
  'Dietitian',
  'Podiatrist',
  'Behaviour Support Practitioner'
]);

const noQualificationClassifications = new Set([
  'Domestic Assistance Worker',
  'Home & Garden Worker'
]);

function createToggleMap(sectionsOrItems) {
  const entries = Array.isArray(sectionsOrItems[0])
    ? sectionsOrItems.flat()
    : sectionsOrItems.flatMap((section) => (section.items ? section.items : [section]));

  return entries.reduce((result, item) => {
    result[item] = false;
    return result;
  }, {});
}

function createEmptyRegistration() {
  return {
    id: `registration-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    registrationType: '',
    registrationNumber: ''
  };
}

function createEmptyQualification() {
  return {
    id: `qualification-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    level: '',
    field: ''
  };
}

function createDefaultFormData() {
  return {
    firstName: '',
    lastName: '',
    preferredName: '',
    type: 'Worker',
    email: '',
    phone: '',
    status: 'Active',
    primaryClassification: '',
    checks: createToggleMap(workerChecks),
    qualifications: [createEmptyQualification()],
    registrations: [createEmptyRegistration()]
  };
}

function isAustralianPhoneNumber(value) {
  const number = String(value || '').replace(/[\s()-.]/g, '');
  const localNumber = number.startsWith('+61') ? `0${number.slice(3)}` : number;
  return /^04\d{8}$/.test(localNumber);
}

function CheckboxSection({ title, items, values, onToggle }) {
  return (
    <div className="compliance-subsection">
      <h4>{title}</h4>
      <div className="checkbox-grid">
        {items.map((item) => (
          <label key={item} className="checkbox-item">
            <input
              type="checkbox"
              checked={Boolean(values[item])}
              onChange={() => onToggle(item)}
            />
            <span>{item}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

const WorkerEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const isSelfShortcut = id === 'me';
  const isNew = location.pathname.endsWith('/workers/new');
  const hasAuthUser = Boolean(user);
  const userRoles = user?.userRoles || [];
  const isAdmin = !hasAuthUser || userRoles.some((role) => role.toLowerCase() === 'admin');

  const [loading, setLoading] = useState(!isNew || isSelfShortcut);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [invitationMessage, setInvitationMessage] = useState(null);
  const [targetWorkerId, setTargetWorkerId] = useState(isSelfShortcut ? null : id);
  const [formData, setFormData] = useState(createDefaultFormData);

  const getUserEmail = (currentUser) => {
    const emailClaim = currentUser?.claims?.find((claim) =>
      ['email', 'emails', 'preferred_username', 'upn'].includes(claim.typ)
    )?.val;
    return (emailClaim || '').toLowerCase();
  };

  const mergeWorkerData = (data) => ({
    ...createDefaultFormData(),
    ...data,
    checks: { ...createDefaultFormData().checks, ...(data.checks || {}) },
    qualifications: data.qualifications?.length
      ? data.qualifications.map((qualification) => ({
        ...createEmptyQualification(),
        level: qualification.level || '',
        field: qualification.field || ''
      }))
      : [createEmptyQualification()],
    registrations: data.registrations?.length
      ? data.registrations.map((registration) => ({ ...createEmptyRegistration(), registrationType: registration.registrationType || '', registrationNumber: registration.registrationNumber || '' }))
      : [createEmptyRegistration()]
  });

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (isNew && hasAuthUser && !isAdmin) {
      setError('Only admins can add a new worker.');
      setLoading(false);
      return;
    }

    if (!isNew) {
      const fetchWorker = async () => {
        try {
          let resolvedWorkerId = id;

          if (isSelfShortcut) {
            const workersResponse = await fetch('/api/workers');
            if (!workersResponse.ok) throw new Error('Failed to fetch workers');
            const workers = await workersResponse.json();
            const userEmail = getUserEmail(user);
            const ownWorker = hasAuthUser
              ? workers.find((worker) => (worker.email || '').toLowerCase() === userEmail)
              : workers[0];

            if (!ownWorker) {
              throw new Error('No worker profile linked to your account.');
            }

            resolvedWorkerId = ownWorker.id;
          }

          const response = await fetch(`/api/workers/${resolvedWorkerId}`);
          if (!response.ok) throw new Error('Failed to fetch worker');
          const data = await response.json();

          const userEmail = getUserEmail(user);
          const targetEmail = (data.email || '').toLowerCase();
          const isSelf = Boolean(userEmail && targetEmail && userEmail === targetEmail);

          if (hasAuthUser && !isAdmin && !isSelf) {
            throw new Error('You can only edit your own worker details.');
          }

          setTargetWorkerId(resolvedWorkerId);
          setFormData(mergeWorkerData(data));
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

      fetchWorker();
    } else {
      setLoading(false);
    }
  }, [id, isNew, isSelfShortcut, isAdmin, user, authLoading, hasAuthUser]);

  if (loading || authLoading) return <div className="loading">Loading worker...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  const canEditPermissions = isAdmin;
  const isProviderSuperAdmin = Boolean(formData.providerSuperAdmin);
  const showQualifications = Boolean(formData.primaryClassification) && !noQualificationClassifications.has(formData.primaryClassification);
  const showRegistrations = registrationClassifications.has(formData.primaryClassification);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleGroupValue = (groupName, item) => {
    setFormData((prev) => ({
      ...prev,
      [groupName]: {
        ...prev[groupName],
        [item]: !prev[groupName][item]
      }
    }));
  };

  const handleListItemChange = (groupName, itemId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [groupName]: prev[groupName].map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  const addListItem = (groupName, factory) => {
    setFormData((prev) => ({
      ...prev,
      [groupName]: [...prev[groupName], factory()]
    }));
  };

  const removeListItem = (groupName, itemId, factory) => {
    setFormData((prev) => {
      const nextItems = prev[groupName].filter((item) => item.id !== itemId);
      return {
        ...prev,
        [groupName]: nextItems.length > 0 ? nextItems : [factory()]
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError(null);

    if (!isAustralianPhoneNumber(formData.phone)) {
      setSubmitError('Enter a valid Australian mobile number.');
      return;
    }

    setSaving(true);
    try {
      const url = isNew ? '/api/workers' : `/api/workers/${targetWorkerId}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to save worker');

      if (isNew && !result.invitationSent) {
        alert(result.invitationError || 'The user was created, but the CIAM sign-up email was not sent.');
      }

      navigate(isSelfShortcut && !isAdmin ? '/provider/dashboard' : '/provider/workers');
    } catch (err) {
      console.error('Error saving worker:', err);
      setSubmitError(err.message || 'Failed to save worker.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(isSelfShortcut && !isAdmin ? '/provider/dashboard' : '/provider/workers');
  };

  const handleInvite = async () => {
    setInvitationMessage(null);
    setInviting(true);
    try {
      const response = await fetch(`/api/workers/${targetWorkerId}/invite`, { method: 'POST' });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Unable to send the sign-up email.');
      setInvitationMessage('CIAM sign-up email sent.');
    } catch (err) {
      setInvitationMessage(err.message || 'Unable to send the sign-up email.');
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="provider-dashboard worker-edit-page">
      <div className="page-header">
        <h1>{isNew ? 'Add Worker' : isSelfShortcut && !isAdmin ? 'Edit My Details' : 'Edit Worker'}</h1>
      </div>

      <div className="form-section worker-form-shell">
        <form onSubmit={handleSubmit} className="worker-form" aria-busy={saving}>
          <section className="compliance-card">
            <div className="section-header-inline">
              <div>
                <h2>Worker Details</h2>
                <p>Maintain contact details and access settings for this worker.</p>
              </div>
            </div>

            {!canEditPermissions && (
              <div className="info-banner">
                You can update your own contact and compliance details. Only admins can change worker type and status.
              </div>
            )}
            {isProviderSuperAdmin && (
              <div className="info-banner">
                Provider Super Admin is controlled by provider provisioning. Email, type, and status cannot be changed here.
              </div>
            )}

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="firstName">
                  First Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">
                  Last Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="preferredName">Preferred Name</label>
                <input
                  type="text"
                  id="preferredName"
                  name="preferredName"
                  value={formData.preferredName}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">
                  Phone <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="0400 000 000 or +61 400 000 000"
                  inputMode="tel"
                  autoComplete="tel"
                />
                <span className="field-hint">Australian mobile number only</span>
              </div>

              <div className="form-group form-group-full">
                <label htmlFor="email">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  disabled={isProviderSuperAdmin}
                />
              </div>

              <div className="form-group">
                <label htmlFor="type">
                  Type <span className="required">*</span>
                </label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                  className="form-input"
                  disabled={!canEditPermissions || isProviderSuperAdmin}
                >
                  <option value="Worker">Worker</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="status">
                  Status <span className="required">*</span>
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                  className="form-input"
                  disabled={!canEditPermissions || isProviderSuperAdmin}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </section>

          <section className="compliance-card">
            <div className="section-header-inline">
              <div>
                <h2>Compliance</h2>
                <p>Capture worker classification, compliance checks and professional registration details.</p>
              </div>
            </div>

            <div className="form-group form-group-full">
              <label htmlFor="primaryClassification">
                Primary Worker Classification <span className="required">*</span>
              </label>
              <select
                id="primaryClassification"
                name="primaryClassification"
                value={formData.primaryClassification}
                onChange={handleChange}
                required
                className="form-input"
              >
                <option value="">Select a classification</option>
                {workerClassifications.map((classification) => (
                  <option key={classification} value={classification}>
                    {classification}
                  </option>
                ))}
              </select>
            </div>

            <CheckboxSection
              title="Checks"
              items={workerChecks}
              values={formData.checks}
              onToggle={(item) => toggleGroupValue('checks', item)}
            />

            {showQualifications && (
              <div className="compliance-subsection">
                <div className="section-header-inline">
                  <div>
                    <h3>Qualifications</h3>
                    <p>Record the worker's qualifications.</p>
                  </div>
                  <button
                    type="button"
                    className="action-button btn-outline compact-action"
                    onClick={() => addListItem('qualifications', createEmptyQualification)}
                  >
                    <Plus size={18} weight="bold" />
                    Add Qualification
                  </button>
                </div>

                <div className="stacked-records">
                  {formData.qualifications.map((qualification, index) => (
                    <div key={qualification.id} className="record-card">
                      <div className="record-card-header">
                        <h4>Qualification {index + 1}</h4>
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() => removeListItem('qualifications', qualification.id, createEmptyQualification)}
                          aria-label={`Remove qualification ${index + 1}`}
                        >
                          <Trash size={18} weight="bold" />
                        </button>
                      </div>
                      <div className="form-grid">
                        <div className="form-group">
                          <label htmlFor={`qualification-level-${qualification.id}`}>Level</label>
                          <select
                            id={`qualification-level-${qualification.id}`}
                            value={qualification.level}
                            onChange={(event) => handleListItemChange('qualifications', qualification.id, 'level', event.target.value)}
                            className="form-input"
                          >
                            <option value="">Select level</option>
                            {qualificationLevels.map((level) => (
                              <option key={level} value={level}>{level}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label htmlFor={`qualification-field-${qualification.id}`}>Field</label>
                          <select
                            id={`qualification-field-${qualification.id}`}
                            value={qualification.field}
                            onChange={(event) => handleListItemChange('qualifications', qualification.id, 'field', event.target.value)}
                            className="form-input"
                          >
                            <option value="">Select field</option>
                            {qualificationFields.map((field) => (
                              <option key={field} value={field}>{field}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {showRegistrations && (
              <div className="compliance-subsection">
                <div className="section-header-inline">
                  <div>
                    <h3>Professional Registration</h3>
                    <p>Track current professional registrations.</p>
                  </div>
                  <button
                    type="button"
                    className="action-button btn-outline compact-action"
                    onClick={() => addListItem('registrations', createEmptyRegistration)}
                  >
                    <Plus size={18} weight="bold" />
                    Add Registration
                  </button>
                </div>

                <div className="stacked-records">
                  {formData.registrations.map((registration, index) => (
                    <div key={registration.id} className="record-card">
                      <div className="record-card-header">
                        <h4>Registration {index + 1}</h4>
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() => removeListItem('registrations', registration.id, createEmptyRegistration)}
                          aria-label={`Remove registration ${index + 1}`}
                        >
                          <Trash size={18} weight="bold" />
                        </button>
                      </div>

                      <div className="form-grid">
                        <div className="form-group form-group-full">
                          <label>Registration Type</label>
                          <select
                            value={registration.registrationType}
                            onChange={(event) => handleListItemChange('registrations', registration.id, 'registrationType', event.target.value)}
                            className="form-input"
                          >
                            <option value="">Select registration type</option>
                            {registrationTypes.map((registrationType) => (
                              <option key={registrationType} value={registrationType}>{registrationType}</option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Registration Number</label>
                          <input
                            type="text"
                            value={registration.registrationNumber}
                            onChange={(event) => handleListItemChange('registrations', registration.id, 'registrationNumber', event.target.value)}
                            className="form-input"
                          />
                        </div>

                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </section>

          {submitError && <div className="form-error" role="alert">{submitError}</div>}

          <div className="form-actions">
            {isAdmin && !isNew && (
              <button type="button" onClick={handleInvite} className="action-button btn-outline" disabled={saving || inviting}>
                {inviting ? 'Sending email...' : 'Send Sign-up Email'}
              </button>
            )}
            <button type="button" onClick={handleCancel} className="action-button btn-outline" disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="action-button btn-solid-blue" disabled={saving}>
              {saving ? 'Saving...' : isNew ? 'Create Worker' : 'Save Changes'}
            </button>
          </div>
          {invitationMessage && <div className="invitation-message" role="status">{invitationMessage}</div>}
        </form>
      </div>
    </div>
  );
};

export default WorkerEdit;